/**
 * Popup lifecycle for OAuth.
 *
 * A popup rather than a full-page redirect so the user keeps their place in the
 * app and the SPA never has to serialise and restore its state mid-flow.
 *
 * Three things go wrong in practice and all three are handled here: the browser
 * blocks the popup, the user closes it without deciding, and the provider errors
 * out. Each gets its own message, because "authorization failed" tells the user
 * nothing about which one happened.
 */

export interface PopupResult {
  code: string
  state: string
}

export class PopupBlockedError extends Error {
  constructor() {
    super(
      'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.',
    )
    this.name = 'PopupBlockedError'
  }
}

export class PopupCancelledError extends Error {
  constructor() {
    super('The sign-in window was closed before authorization finished.')
    this.name = 'PopupCancelledError'
  }
}

export class OAuthProviderError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'OAuthProviderError'
  }
}

/** What CallbackPage posts back to the opener. */
export interface CallbackMessage {
  source: 'context-graph-oauth'
  code?: string
  state?: string
  error?: string
  error_description?: string
}

const POPUP_FEATURES = 'width=620,height=760,menubar=no,toolbar=no,location=yes'
const TIMEOUT_MS = 5 * 60 * 1000

export function openAuthPopup(url: string): Promise<PopupResult> {
  // Opened synchronously from the click that triggered it — deferring past an
  // await is the most common reason a popup gets blocked.
  const popup = window.open(url, 'context-graph-oauth', POPUP_FEATURES)
  if (!popup) return Promise.reject(new PopupBlockedError())

  return new Promise<PopupResult>((resolve, reject) => {
    let settled = false

    const cleanup = () => {
      settled = true
      window.removeEventListener('message', onMessage)
      clearInterval(closedPoll)
      clearTimeout(timeout)
    }

    const onMessage = (event: MessageEvent) => {
      // Only trust messages from our own origin: the callback page runs in the
      // popup on this origin, so anything else is not part of this flow.
      if (event.origin !== window.location.origin) return

      const data = event.data as CallbackMessage | undefined
      if (!data || data.source !== 'context-graph-oauth') return

      cleanup()
      try {
        popup.close()
      } catch {
        // Already closed itself; nothing to do.
      }

      if (data.error) {
        reject(
          new OAuthProviderError(
            data.error_description || `The provider refused authorization: ${data.error}`,
          ),
        )
        return
      }
      if (!data.code || !data.state) {
        reject(new OAuthProviderError('The provider returned no authorization code.'))
        return
      }
      resolve({ code: data.code, state: data.state })
    }

    window.addEventListener('message', onMessage)

    // There is no event for "user closed the popup", so it has to be polled.
    const closedPoll = setInterval(() => {
      if (settled) return
      if (popup.closed) {
        cleanup()
        reject(new PopupCancelledError())
      }
    }, 500)

    const timeout = setTimeout(() => {
      if (settled) return
      cleanup()
      try {
        popup.close()
      } catch {
        /* ignore */
      }
      reject(new OAuthProviderError('Authorization timed out. Please try again.'))
    }, TIMEOUT_MS)
  })
}
